import React, { useState } from 'react';
import { Button, Card, Col, Modal, Row, Skeleton, Tag, Typography } from 'antd';
import {
  useAccountPayableObservationDelete,
  useAccountPayableObservationList,
} from '@/core/domain/financial/AccountPayableObservation/AccountPayableObservation.hooks';
import { DeleteIcon, EditIcon, Group, UserAvatar } from '@/components/_common';
import { FieldTimeOutlined } from '@ant-design/icons';
import moment from 'moment';
import AccountPayableObservationModal from '../AccountPayableObservationModal';
import { useDisclosure, useWindowSize } from '@/hooks';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { AccountPayableObservation } from '@/core/domain/financial/AccountPayableObservation/AccountPayableObservation.types';
import Meta from 'antd/lib/card/Meta';

interface Props {
  installmentPayableId: number;
}

const AccountPayableObservationList: React.FC<Props> = ({
  installmentPayableId,
}) => {
  const { isMobile } = useWindowSize();
  const [isObservationModalOpen, setObservationModalOpen] = useDisclosure(
    false,
    {
      onClose: () => {
        setSelectedObservation(undefined);
      },
    },
  );
  const [selectedObservation, setSelectedObservation] =
    useState<AccountPayableObservation>();

  const { data, isLoading } = useAccountPayableObservationList({
    installmentPayableId,
  });

  const { mutateAsync: handleDelete } = useAccountPayableObservationDelete();

  const handleRemove = async (observationId: number) => {
    Modal.confirm({
      title: 'Remover Observação',
      content: 'Deseja realmente remover a observação selecionada?',
      onOk: async () => await handleDelete(observationId),
    });
  };

  const openObservationModal = (observation?: AccountPayableObservation) => {
    setSelectedObservation(observation);
    setObservationModalOpen.open();
  };

  return (
    <>
      <SpaceForButton
        justifyContent="space-between"
        style={{ marginBottom: 24 }}
      >
        <Typography.Title level={4} style={{ marginBottom: 0 }}>
          Observações
        </Typography.Title>

        <Button block type="primary" onClick={() => openObservationModal()}>
          Nova observação
        </Button>
      </SpaceForButton>

      <Skeleton avatar title={false} loading={isLoading} active>
        <Row gutter={[12, 12]}>
          {data?.map((item, index) => (
            <Col span={24} key={index}>
              <Card
                style={{ borderRadius: 4 }}
                actions={
                  isMobile
                    ? [
                        <EditIcon
                          iconProps={{
                            onClick: () => openObservationModal(item),
                          }}
                        />,
                        <DeleteIcon onClick={() => handleRemove(item.id)} />,
                      ]
                    : undefined
                }
              >
                <Meta
                  className="meta-custom"
                  avatar={
                    <UserAvatar
                      picture={item.employeePhoto}
                      gender={item.employeeGender}
                    />
                  }
                  title={
                    <Row gutter={[12, 12]}>
                      <Col flex={1}>{item.employeeName}</Col>

                      {!isMobile && (
                        <Col>
                          <Tag>
                            {moment(item.creationDate).format('DD MMM. YYYY')}
                          </Tag>

                          <Tag>
                            <FieldTimeOutlined />
                            {moment(item.creationDate).format('HH:mm')}
                          </Tag>
                        </Col>
                      )}

                      {!isMobile && (
                        <>
                          <Col>
                            <EditIcon
                              iconProps={{
                                onClick: () => openObservationModal(item),
                              }}
                            />
                          </Col>

                          <Col>
                            <DeleteIcon onClick={() => handleRemove(item.id)} />
                          </Col>
                        </>
                      )}
                    </Row>
                  }
                  description={
                    <>
                      <Typography.Text>{item.observation}</Typography.Text>

                      {isMobile && (
                        <Group position="center" style={{ padding: 12 }}>
                          <Tag>
                            {moment(item.creationDate).format('DD MMM. YYYY')}
                          </Tag>

                          <Tag>
                            <FieldTimeOutlined />
                            {moment(item.creationDate).format('HH:mm')}
                          </Tag>
                        </Group>
                      )}
                    </>
                  }
                />
              </Card>
            </Col>
          ))}

          {!data?.length && (
            <Typography.Text style={{ padding: '0 24px 0 24px' }}>
              Não há observações
            </Typography.Text>
          )}
        </Row>
      </Skeleton>

      <AccountPayableObservationModal
        installmentPayableId={installmentPayableId}
        open={isObservationModalOpen}
        onCancel={setObservationModalOpen.close}
        observationData={selectedObservation}
      />
    </>
  );
};

export default AccountPayableObservationList;
